import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from 'components/button';
import PillTabs from 'components/tabs/PillTabs';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import SlideOverHeader from 'components/SlideOver/component/header';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import { Loading2 } from 'assets/icons';
import useText from 'hooks/useText';
import {
  filterProspects,
  globalTabs,
  inDayValues,
} from 'pages/prospection/callListItems';
import GlobalListRow from './GlobalListRow';

const GlobalListPanel = ({
  open,
  onClose,
  prospects,
  loading,
  counts,
  onAdd,
  onShowAllProspects,
}) => {
  const { text } = useText('prospection');
  const titleId = useId();
  const [tab, setTab] = useState('discovery');
  const [query, setQuery] = useState('');
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Read at opening only: adding a prospect changes the counts, and the tab
  // must not jump under the user's hands.
  const countsRef = useRef(counts);
  countsRef.current = counts;
  // The page mounts the panel before the day has loaded, so counts may be
  // missing: the opening reset waits for them.
  const countsLoaded = Boolean(counts);

  useEffect(() => {
    if (!open || !countsLoaded) return;
    setTab(globalTabs(countsRef.current).initialTab);
    setQuery('');
    setAddedIds(new Set());
  }, [open, countsLoaded]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleAdd = useCallback(
    async (prospect) => {
      await onAdd(prospect);
      setAddedIds((previous) => new Set(previous).add(prospect.company.id));
    },
    [onAdd],
  );

  const visible = useMemo(
    () => filterProspects(prospects, { type: tab, query }),
    [prospects, tab, query],
  );

  if (!open || !counts) return null;

  const tabCounts = globalTabs(counts);
  const tabs = [
    { id: 'discovery', label: text('global.tabs.discovery'), count: tabCounts.discovery },
    { id: 'followup', label: text('global.tabs.followup'), count: tabCounts.followup },
  ];

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-16" role="status">
          <span className="animate-spin">
            <Loading2 wh={28} color="#9CA3AF" />
          </span>
        </div>
      );
    }
    if (visible.length === 0) {
      return query.trim() ? (
        <CompactEmptyState
          compact
          glyph="searchX"
          title={text('global.emptySearchTitle')}
          description={text('global.emptySearchText')}
        />
      ) : (
        <CompactEmptyState
          compact
          glyph="squareCheck"
          title={text(`global.emptyTab.${tab}`)}
          description={text('global.emptyTabText')}
        />
      );
    }
    return (
      <ul>
        {visible.map((prospect) => (
          <GlobalListRow
            key={prospect.company.id}
            prospect={prospect}
            added={addedIds.has(prospect.company.id)}
            onAdd={handleAdd}
          />
        ))}
      </ul>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-30">
      <div
        data-testid="global-list-veil"
        className="absolute inset-0 bg-gray-900 opacity-50 motion-safe:animate-veilIn"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute right-0 flex h-full min-h-0 w-full flex-col bg-white lg:w-[600px] motion-safe:animate-slideR"
      >
        <SlideOverHeader hide={onClose} closeLabel={text('sheet.close')}>
          <span id={titleId}>{text('global.title')}</span>
        </SlideOverHeader>
        <SlideOverBody className="flex flex-col gap-4">
          <p className="ft-sm text-gray-500">{text('global.description')}</p>
          <p className="ft-sm font-medium text-gray-700">
            {text('global.inDay', inDayValues(counts))}
          </p>
          <PillTabs tabs={tabs} activeTab={tab} onChange={setTab} variant="counter" greyZero />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text('global.search')}
            aria-label={text('global.search')}
            className="w-full rounded-md border border-gray-200 px-3 py-2 ft-sm"
          />
          {renderList()}
        </SlideOverBody>
        <SlideOverFooter>
          <Button color="SECONDARY" onClick={onShowAllProspects}>
            {text('global.showAll')}
          </Button>
          <Button onClick={onClose}>{text('global.done')}</Button>
        </SlideOverFooter>
      </div>
    </div>,
    document.body,
  );
};

export default GlobalListPanel;
